/**
 * Shared cost assumptions for Scenario Cost Analysis.
 * Configuration layer only — does not change the physical model.
 * Buildings and transport cost models use these when provided via options.
 */

import {
  ELECTRICITY_PRICE_PER_KWH,
  NATURAL_GAS_PRICE_PER_THERM,
  WEATHERIZATION_COST_PER_PARTICIPATING_HOME,
  RES_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT,
  COM_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT_OR_EQUIVALENT,
  RES_HEAT_PUMP_COST_PER_UNIT,
  COM_HEAT_PUMP_COST_PER_UNIT,
  AVG_THERMS_PER_RES_UNIT,
  AVG_THERMS_PER_COM_EQUIVALENT_UNIT,
  DISCOUNT_RATE,
  HEAT_PUMP_REBATE,
  WEATHERIZATION_REBATE,
  type BuildingsCostAssumptions,
} from './buildingsCostModel';
import {
  GASOLINE_PRICE_PER_GALLON_DEFAULT,
  EV_INCREMENTAL_COST,
  CHARGER_COST_PER_EV,
} from './transportCostModel';

/** User-adjustable cost assumptions (global defaults). */
export interface CostAssumptions {
  electricityPricePerKwh: number;
  naturalGasPricePerTherm: number;
  gasolinePricePerGallon: number;
  resHeatPumpIncrementalCostPerUnit: number;
  comHeatPumpIncrementalCostPerUnit: number;
  avgThermsPerResUnit: number;
  avgThermsPerComEquivalentUnit: number;
  evIncrementalCost: number;
  chargerCostPerEv: number;
  weatherizationCostPerParticipatingHome: number;
  residentialReachCodeCostPerNewUnit: number;
  commercialReachCodeCostPerNewUnit: number;
  discountRate: number;
  heatPumpRebate: number;
  weatherizationRebate: number;
}

/** Centralized default values (from buildings + transport model constants). */
export const DEFAULT_COST_ASSUMPTIONS: CostAssumptions = {
  electricityPricePerKwh: ELECTRICITY_PRICE_PER_KWH,
  naturalGasPricePerTherm: NATURAL_GAS_PRICE_PER_THERM,
  gasolinePricePerGallon: GASOLINE_PRICE_PER_GALLON_DEFAULT,
  resHeatPumpIncrementalCostPerUnit: RES_HEAT_PUMP_COST_PER_UNIT,
  comHeatPumpIncrementalCostPerUnit: COM_HEAT_PUMP_COST_PER_UNIT,
  avgThermsPerResUnit: AVG_THERMS_PER_RES_UNIT,
  avgThermsPerComEquivalentUnit: AVG_THERMS_PER_COM_EQUIVALENT_UNIT,
  evIncrementalCost: EV_INCREMENTAL_COST,
  chargerCostPerEv: CHARGER_COST_PER_EV,
  weatherizationCostPerParticipatingHome: WEATHERIZATION_COST_PER_PARTICIPATING_HOME,
  residentialReachCodeCostPerNewUnit: RES_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT,
  commercialReachCodeCostPerNewUnit: COM_REACH_CODE_INCREMENTAL_COST_PER_NEW_UNIT_OR_EQUIVALENT,
  discountRate: DISCOUNT_RATE,
  heatPumpRebate: HEAT_PUMP_REBATE,
  weatherizationRebate: WEATHERIZATION_REBATE,
};

/** Per-location overrides for energy prices. Blank = use global default. */
export interface LocationPriceOverrides {
  electricityPricePerKwh?: number;
  naturalGasPricePerTherm?: number;
  gasolinePricePerGallon?: number;
}

/** Map shared CostAssumptions into buildings cost model overrides (Portfolio + pathways). */
export function costAssumptionsToBuildingsOverrides(a: CostAssumptions): BuildingsCostAssumptions {
  return {
    electricityPricePerKwh: a.electricityPricePerKwh,
    naturalGasPricePerTherm: a.naturalGasPricePerTherm,
    weatherizationCostPerHome: a.weatherizationCostPerParticipatingHome,
    resReachIncrementalCostPerUnit: a.residentialReachCodeCostPerNewUnit,
    comReachIncrementalCostPerUnit: a.commercialReachCodeCostPerNewUnit,
    resHeatPumpIncrementalCostPerUnit: a.resHeatPumpIncrementalCostPerUnit,
    comHeatPumpIncrementalCostPerUnit: a.comHeatPumpIncrementalCostPerUnit,
    avgThermsPerResUnit: a.avgThermsPerResUnit,
    avgThermsPerComEquivalentUnit: a.avgThermsPerComEquivalentUnit,
    discountRate: a.discountRate,
    heatPumpRebate: a.heatPumpRebate,
    weatherizationRebate: a.weatherizationRebate,
  };
}
